import { isNil } from 'lodash';

import type { COLORS } from '~/components/core/StatusChipCore';
import type {
  DocumentRequestListResponseDocumentRequestDTO,
  DocumentRequestListResponseDocumentRequestFileDTO,
} from '~/server_shared/openapi/data-contracts';
import {
  DocumentRequestListResponseDocumentRequestFileScanState,
  DocumentRequestListResponseDocumentRequestStatus,
} from '~/server_shared/openapi/data-contracts';

import { TEXTS } from './texts';

export type DocumentRequest = DocumentRequestListResponseDocumentRequestDTO;
export type DocumentRequestFile = DocumentRequestListResponseDocumentRequestFileDTO;
export const ScanState = DocumentRequestListResponseDocumentRequestFileScanState;
export type ScanState = DocumentRequestListResponseDocumentRequestFileScanState;
export const LinkStatus = DocumentRequestListResponseDocumentRequestStatus;
export type LinkStatus = DocumentRequestListResponseDocumentRequestStatus;

interface ChipDisplay {
  label: string;
  color: COLORS;
}

// Typed over the generated enums, so a member added on the server is a compile error here.
const LINK_STATUS_CHIP: Record<LinkStatus, ChipDisplay> = {
  [LinkStatus.PENDING]: { label: TEXTS.LINK_STATUS_PENDING, color: 'blue' },
  [LinkStatus.UPLOADING]: { label: TEXTS.LINK_STATUS_UPLOADING, color: 'orange' },
  [LinkStatus.SUBMITTED]: { label: TEXTS.LINK_STATUS_SUBMITTED, color: 'green' },
  [LinkStatus.CANCELLED]: { label: TEXTS.LINK_STATUS_CANCELLED, color: 'grey' },
};

const EXPIRED_CHIP: ChipDisplay = { label: TEXTS.LINK_STATUS_EXPIRED, color: 'grey' };

// The server sets is_expired only for a PENDING link past its expiry; this only labels it.
export const getLinkStatusChip = (request: DocumentRequest): ChipDisplay =>
  request.is_expired ? EXPIRED_CHIP : LINK_STATUS_CHIP[request.status];

export const SCAN_STATE_CHIP: Record<ScanState, ChipDisplay> = {
  [ScanState.PENDING]: { label: TEXTS.SCAN_PENDING, color: 'yellow' },
  [ScanState.CLEAN]: { label: TEXTS.SCAN_CLEAN, color: 'green' },
  [ScanState.SUSPICIOUS]: { label: TEXTS.SCAN_SUSPICIOUS, color: 'red' },
  [ScanState.ERROR]: { label: TEXTS.SCAN_ERROR, color: 'orange' },
};

// Mirrors the server's refusals: download_url and attach answer 409 for anything but CLEAN, attach
// also for an attached file, rescan for anything but ERROR. SUSPICIOUS has no action at all.
export const canPreview = (file: DocumentRequestFile): boolean => file.scan_state === ScanState.CLEAN;

export const canAttach = (file: DocumentRequestFile): boolean =>
  file.scan_state === ScanState.CLEAN && isNil(file.attached_document_id);

export const canRescan = (file: DocumentRequestFile): boolean => file.scan_state === ScanState.ERROR;

type CopyableDocumentRequest = DocumentRequest & { url: string };

// The server sends the url only while the link can still be used.
export const canCopyLink = (request: DocumentRequest): request is CopyableDocumentRequest => !isNil(request.url);

export const isCancelOnlyLink = (request: DocumentRequest): boolean => request.status === LinkStatus.UPLOADING;
